import React, { useEffect } from "react"
import { AuthState } from "../../context/usePublicViewContext"
import { useCreatePerson } from "../../hooks/People/useCreatePerson"
import { useSaveCustomer } from "../../hooks/Costumer/useSaveCustomer"
import { useParams } from "react-router-dom"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { CustomerEntity } from "../../Objects/Customer/CustomerEntity"
import { useTranslation } from "react-i18next"
import ModalPortal from "../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import InputField from "../../components/InputField/InputField"
import { INPUT_SIZE } from "../../configuration/InputSize"
import { useDownloadContract } from "../../hooks/Contract/useDownloadContract"
import { EContractProfile } from "../../Enum/EContractProfile"
import { companyMemory } from "../../Objects/Memory/Memory"
import { EGenderType } from "../../Enum/EGenderType"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { getPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"

interface SignupViewProps {
  onSuccess: (authState: AuthState) => void
  onGoToLogin: () => void
  initialCpfOrPhone?: string
}

export const SignupView: React.FC<SignupViewProps> = ({ onSuccess, onGoToLogin, initialCpfOrPhone = "" }) => {
  const { t } = useTranslation()

  const theme = getPublicMenuTheme(companyMemory)

  const isLegacyTheme = isLegacyPublicMenuTheme(theme)

  const { restaurantSlug } = useParams<{ restaurantSlug: string }>()

  const { HandleCreateCustomer } = useSaveCustomer()

  const { handleDownloadContractAsync } = useDownloadContract()

  const {
    name,
    cpf,
    phone,
    gender,
    setCpf,
    setPhone,
    setGender,
    handleNameChange,
    handleCpfChange,
    handlePhoneChange,
    handleSubmitForm,
  } = useCreatePerson()

  const isLikelyPhoneNumber = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "")

    if (digitsOnly.length === 10) {
      return true
    }

    if (digitsOnly.length === 11) {
      return digitsOnly[2] === "9"
    }

    return false
  }

  useEffect(() => {
    const sanitizedValue = initialCpfOrPhone.trim()

    if (!sanitizedValue) {
      return
    }

    if (isLikelyPhoneNumber(sanitizedValue)) {
      setCpf("")
      setPhone(sanitizedValue)

      return
    }

    setPhone("")
    setCpf(sanitizedValue)
  }, [initialCpfOrPhone, setCpf, setPhone])

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const person = handleSubmitForm()

    if (!person) {
      e.currentTarget.reportValidity()

      return
    }

    var customer = new CustomerEntity({
      personEntity: person,
    })

    await HandleCreateCustomer(
      API_ROUTES.AUTH.CREATE_LOGIN_CUSTOMER(restaurantSlug),
      customer,
      (authState: AuthState) => {
        onSuccess(authState)
      },
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div
            className={defaultStyle.alert}
            style={
              isLegacyTheme
                ? undefined
                : {
                    backgroundColor: theme.surfaceColor,
                    borderColor: theme.borderColor,
                    color: theme.textColor,
                  }
            }
          >
            <form onSubmit={handleRegister} className="space-y-5 p-4 ">
              <Animate type={AnimationTypes.slideUpIn} duration="1.4s">
                <div className="text-center mb-5">
                  {companyMemory.iconUrl.length > 0 ? (
                    <img
                      src={companyMemory.iconUrl.toClientRequest()}
                      alt="Logo da empresa"
                      className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-orange-600/20 object-cover"
                    />
                  ) : (
                    <div
                      className="w-20 h-20 bg-orange-600 text-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-orange-600/20"
                      style={isLegacyTheme ? undefined : { backgroundColor: theme.primaryColor }}
                    >
                      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                        />
                      </svg>
                    </div>
                  )}

                  <h2
                    className="text-3xl font-black text-gray-950 tracking-tighter leading-none mb-5"
                    style={isLegacyTheme ? undefined : { color: theme.restaurantNameColor }}
                  >
                    {t("publicMenu:signup.title")}
                  </h2>

                  <p
                    className="text-gray-400 font-bold uppercase text-[10px] tracking-widest"
                    style={isLegacyTheme ? undefined : { color: theme.restaurantDescriptionColor }}
                  >
                    {t("publicMenu:signup.subtitle")}
                  </p>
                </div>
              </Animate>

              <Animate type={AnimationTypes.slideUpIn} duration="1.2s">
                <div className="flex flex-col gap-4">
                  <InputField
                    type="text"
                    value={name}
                    onChange={handleNameChange}
                    placeholder={t("publicMenu:signup.full_name_placeholder")}
                    required
                    descriptionKey="interactionDescriptions:publicMenu.pages.signupView.input.fullNamePlaceholder"
                  />

                  <InputField
                    type="text"
                    value={cpf}
                    minLength={INPUT_SIZE.PERSON.cpf}
                    maxLength={INPUT_SIZE.PERSON.cpf}
                    inputMode="numeric"
                    pattern="[0-9]{11}"
                    onChange={handleCpfChange}
                    onInvalidMessage={t("person:label_person_cpf_onInvalidMessage")}
                    placeholder={t("publicMenu:signup.cpf")}
                    required
                    descriptionKey="interactionDescriptions:publicMenu.pages.signupView.input.cpf"
                  />

                  <InputField
                    type="text"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="(00) 90000-0000"
                    required
                    descriptionKey="interactionDescriptions:publicMenu.pages.signupView.input.input"
                  />

                  <div className="space-y-2">
                    <GenericDropDown
                      initialState={String(gender)}
                      legend={t("publicMenu:signup.sex")}
                      data={[
                        { label: "Homem", value: String(EGenderType.Man) },
                        { label: "Mulher", value: String(EGenderType.Woman) },
                      ]}
                      handlerFuncion={(e) => setGender(Number(e.target.value) as EGenderType)}
                      descriptionKey="interactionDescriptions:publicMenu.pages.signupView.select.sex"
                    />

                    <p className="text-[11px] text-gray-500">{t("publicMenu:signup.sex1")}</p>
                  </div>
                </div>
              </Animate>

              <Animate type={AnimationTypes.slideUpIn} duration="1.5s">
                <div
                  className="rounded-lg border-2 border-orange-100 px-3 py-2 text-[11px] text-gray-600"
                  style={
                    isLegacyTheme
                      ? undefined
                      : {
                          borderColor: theme.borderColor,
                          backgroundColor: theme.secondaryColor,
                          color: theme.textColor,
                        }
                  }
                >
                  <span className="font-semibold text-green-700">✓</span> {t("publicMenu:signup.contract_auto_accept")}{" "}
                  <button
                    type="button"
                    onClick={() => handleDownloadContractAsync(EContractProfile.Customer)}
                    className="font-bold text-orange-600 hover:underline"
                    style={isLegacyTheme ? undefined : { color: theme.accentColor }}
                    data-interaction-description-key="interactionDescriptions:publicMenu.pages.signupView.button.handleDownloadContractAsyncEContractProfileCustomer"
                  >
                    {t("publicMenu:signup.contract_download_link")}
                  </button>
                </div>
              </Animate>

              <Animate type={AnimationTypes.slideUpIn} duration="2.4s">
                <button
                  className="w-full h-14 ring-2 ring-orange-500 text-orange-500 rounded-md font-black text-[12px] uppercase tracking-[0.25em] shadow-2xl shadow-gray-200 active:scale-95 transition-all mt-6"
                  style={
                    isLegacyTheme
                      ? undefined
                      : { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
                  }
                  data-interaction-description-key="interactionDescriptions:publicMenu.pages.signupView.button.finishRegistration"
                >
                  {t("publicMenu:signup.finish_registration")}
                </button>
              </Animate>

              <Animate type={AnimationTypes.slideUpIn} duration="2.8s">
                <button
                  type="button"
                  onClick={onGoToLogin}
                  className="w-full text-[11px] font-black text-gray-500 uppercase tracking-widest hover:text-orange-600 transition-colors"
                  style={isLegacyTheme ? undefined : { color: theme.textColor, opacity: 0.7 }}
                  data-interaction-description-key="interactionDescriptions:publicMenu.pages.signupView.button.onGoToLogin"
                >
                  {t("publicMenu:signup.back_to_identification")}
                </button>
              </Animate>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
